import { SERVER_DATE_FORMAT, SERVER_DATE_TIME_FORMAT } from "@constants/date-formats";
import { resources } from "@constants/hardcoded-strings";
import {
    REPORTS_FRAUD_REPORT_URL,
    REPORTS_TRANSACTIONS_URL,
    REPORTS_WORKFLOW_REPORT_URL,
    VERIFICATION_SERVICES_URL,
    WORKFLOWS_DETAILS_URL
} from "@constants/routes";
import { SpinnerModal } from "@containers/common/modals/spinner-modal/spinner-modal";
import { TableCellWithDropdown } from "@containers/common/table-cell-with-dropdown/table-cell-with-dropdown";
import { VerificationLinkProviderModal } from "@containers/modals/verification-link-provider-modal/verification-link-provider-modal";
import { useModalContext } from "@context/use-modal-context";
import { ChangeRequestStatusTypes } from "@enums/change-request-status-types";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ModalTypes } from "@enums/modal-types";
import { TimeRangeTypes } from "@enums/time-range-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { CellDropdownItem } from "@idw-types/common";
import { AxnAdminUserRpProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-rp-property";
import { FormattedEmailTemplate } from "@idw-types/data/email-template";
import {
    AxnAttributeTypeField,
    AxnAvailablePropertyCategory,
    AxnCompany,
    AxnCountry,
    AxnIdentityProvider,
    AxnRelyingParty,
    AxnAttributeTypeFieldCustomization,
} from "@idw-types/data/reference-data/axn";
import {
    SelfServiceVerificationAttribute,
    AttributeClassification,
    AttributeLabel,
    AttributeType,
    RpServiceAttributePropertyMap,
    RpServiceAttributeProperty
} from "@idw-types/data/reference-data/generic";
import { FormattedWorkflowConfiguration } from "@idw-types/data/workflow-configuration";
import { getWorkflowConfigurationsReduxRequest, updateWorkflowChangeRequestReduxRequest } from "@redux-requests/workflows";
import { store } from "@redux/store";
import { createWorkflowChangeRequestRequest, exportWorkflowConfigurationRequest } from "@requests/service-configuration";
import { displaySuccessModal, logAndDisplayErrorModal } from "@utils/app-alert-util";
import { getRemainingChangeRequestStatuses } from "@utils/change-request-modal-util/common-change-request-modal-util";
import {
    clickWorkflowChangeRequestActionHandler,
    deployWorkflow
} from "@utils/change-request-modal-util/workflow-change-request-modal-util";
import { getTimeRange } from "@utils/date-util";
import { downloadWorkflowConfigurationFile } from "@utils/file-util";
import { navigateToUtil, openToNewTabUtil } from "@utils/navigation-util";
import { injectWorkflowId } from "@utils/parameter-injector";
import { getTableStateQueryParameters } from "@utils/table-util";
import format from "date-fns/format";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createSearchParams, useLocation, useNavigate } from "react-router-dom";
import { ArchiveVerificationWorkflowModal } from "../archive-verification-workflow-modal/archive-verification-workflow-modal";
import { DeleteVerificationWorkflowModal } from "../delete-verification-workflow-modal/delete-verification-workflow-modal";
import { RestoreVerificationWorkflowModal } from "../restore-verification-workflow-modal/restore-verification-workflow-modal";
import styles from "./workflows-table-cell.less";
import { getSessionState } from "@redux/selectors/root-selectors";

type WorkflowsTableCellProps = {
    value: string;
    workflowConfiguration: FormattedWorkflowConfiguration;
    canRestoreDueToLimits: boolean;
    companies: Array<AxnCompany>;
    relyingParties: Array<AxnRelyingParty>;
    axnCountries: Array<AxnCountry>;
    axnIdentityProviders: Array<AxnIdentityProvider>;
    axnAvailablePropertyCategories: Array<AxnAvailablePropertyCategory>;
    emailTemplates: Array<FormattedEmailTemplate>;
    axnAttributeTypeFields: Array<AxnAttributeTypeField>;
    axnAttributeTypeFieldCustomizations: Array<AxnAttributeTypeFieldCustomization>;
    selfServiceVerificationAttributes: Array<SelfServiceVerificationAttribute>;
    axnAdminUserRPProperties: Array<AxnAdminUserRpProperty>;
    attributeTypes: Array<AttributeType>;
    attributeLabels: Array<AttributeLabel>;
    attributeClassifications: Array<AttributeClassification>;
    rpServiceAttributeProperties: Array<RpServiceAttributeProperty>;
    rpServiceAttributePropertyMaps: Array<RpServiceAttributePropertyMap>;
};

type WorkflowsModalProps = {
    modalType: ModalTypes;
    resultModalType: ModalTypes;
    title: string;
    description: string;
    buttonLabel: string;
};

export const WorkflowsTableCell = (props: WorkflowsTableCellProps) => {
    const {
        value,
        workflowConfiguration,
        canRestoreDueToLimits,
        companies,
        relyingParties,
        axnCountries,
        axnIdentityProviders,
        axnAvailablePropertyCategories,
        emailTemplates,
        axnAttributeTypeFields,
        axnAttributeTypeFieldCustomizations,
        selfServiceVerificationAttributes,
        axnAdminUserRPProperties,
        attributeClassifications,
        attributeLabels,
        attributeTypes,
        rpServiceAttributeProperties,
        rpServiceAttributePropertyMaps
    } = props;

    const navigate = useNavigate();
    const modalContext = useModalContext();
    const routerLocation = useLocation();
    const dispatch = useDispatch();
    const { session } = useSelector(getSessionState);

    const renderWorkflowModal = (workflowsModalProps: WorkflowsModalProps) => {
        if (workflowConfiguration.primaryRPServiceID) {
            const renderVerificationLinkProviderModal = (onModalClose: () => void) => {
                return (
                    <VerificationLinkProviderModal
                        {...workflowsModalProps}
                        idPrefix={"workflow"}
                        onModalClose={onModalClose}
                        rpServiceId={`${workflowConfiguration.primaryRPServiceID}`}
                        canModifySelfServiceVerificationExpiration={workflowConfiguration.permissions.canModifySelfServiceVerificationExpiration}
                        axnCompanies={companies}
                        axnCountries={axnCountries}
                        axnIdentityProviders={axnIdentityProviders}
                        axnAvailablePropertyCategories={axnAvailablePropertyCategories}
                        emailTemplates={emailTemplates}
                        axnAttributeTypeFields={axnAttributeTypeFields}
                        axnAttributeTypeFieldCustomizations={axnAttributeTypeFieldCustomizations}
                        selfServiceVerificationAttributes={selfServiceVerificationAttributes}
                        attributeClassifications={attributeClassifications}
                        attributeLabels={attributeLabels}
                        attributeTypes={attributeTypes}
                        rpServiceAttributeProperties={rpServiceAttributeProperties}
                        rpServiceAttributePropertyMaps={rpServiceAttributePropertyMaps}
                    />
                );
            };

            modalContext.setModalState({
                modalType: workflowsModalProps.modalType,
                renderFunction: renderVerificationLinkProviderModal
            });
        } else {
            logAndDisplayErrorModal(
                dispatch,
                null,
                resources.pages.workflows.errors.noApiKeysConfigured.title,
                resources.pages.workflows.errors.noApiKeysConfigured.message,
            );
        }
    }

    const onClickTryNow = () => {
        renderWorkflowModal({
            modalType: ModalTypes.TryNow,
            resultModalType: ModalTypes.TryNowResult,
            title: resources.modals.verificationLinkProvider.titles.tryNow,
            description: resources.modals.verificationLinkProvider.descriptions.tryNow,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.tryNow,
        });
    };

    const onClickCopyLink = () => {
        renderWorkflowModal({
            modalType: ModalTypes.CopyLink,
            resultModalType: ModalTypes.CopyLinkResult,
            title: resources.modals.verificationLinkProvider.titles.copyLink,
            description: resources.modals.verificationLinkProvider.descriptions.copyLink,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.copyLink,
        });
    };

    const onClickSendEmailWithLink = () => {
        renderWorkflowModal({
            modalType: ModalTypes.SendEmailWithLink,
            resultModalType: ModalTypes.SendEmailWithLinkResult,
            title: resources.modals.verificationLinkProvider.titles.sendEmail,
            description: resources.modals.verificationLinkProvider.descriptions.sendEmail,
            buttonLabel: resources.modals.verificationLinkProvider.buttonLabels.sendEmail,
        });
    };

    const onClickOpen = () => {
        const tableState = store.getState().tableSettings.workflows;
        const url = injectWorkflowId(WORKFLOWS_DETAILS_URL, workflowConfiguration.id);
        let tableStateQueryParams = getTableStateQueryParameters(tableState);
        if (tableStateQueryParams.length > 0) {
            tableStateQueryParams = tableStateQueryParams.substring(1, tableStateQueryParams.length);
        }
        openToNewTabUtil(`${url}?${tableStateQueryParams}`, routerLocation.pathname);
    };


    const onSubmitCreateWorkflowChangeRequest = async (onModalClose: () => void) => {
        const { data, error } = await createWorkflowChangeRequestRequest(workflowConfiguration.id);
        if (data?.changeRequest) {
            displaySuccessModal(
                dispatch,
                resources.requests.createWorkflowChangeRequest.success.title,
                resources.requests.createWorkflowChangeRequest.success.message
            );
            const url = injectWorkflowId(WORKFLOWS_DETAILS_URL, data.changeRequest.workflowID);
            onModalClose();
            navigateToUtil(url, navigate, routerLocation.pathname);
            return data.changeRequest;
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.createWorkflowChangeRequest.error.title,
                resources.requests.createWorkflowChangeRequest.error.message,
                error
            );
        }
        return null;
    };

    const onClickCreateAModifyChangeRequest = () => {
        return clickWorkflowChangeRequestActionHandler(
            onSubmitCreateWorkflowChangeRequest,
            modalContext,
            workflowConfiguration.name,
            workflowConfiguration.id,
            workflowConfiguration.changeRequestStatus,
            ModalTypes.CreateWorkflowChangeRequest
        );
    };

    const onSubmitCancelWorkflowChangeRequest = async (onModalClose: () => void) => {
        const response = await updateWorkflowChangeRequestReduxRequest(dispatch)(
            workflowConfiguration.id,
            null,
            ChangeRequestStatusTypes.CANCELED,
            companies,
            relyingParties
        );
        if (response) {
            onModalClose();
        }
        return response;
    };

    const onClickCancelChangeRequest = () => {
        return clickWorkflowChangeRequestActionHandler(
            onSubmitCancelWorkflowChangeRequest,
            modalContext,
            workflowConfiguration.name,
            workflowConfiguration.id,
            workflowConfiguration.changeRequestStatus,
            ModalTypes.CancelWorkflowChangeRequest
        );
    };

    const onSubmitDeployNowWorkflowChangeRequest = async (onModalClose: () => void) => {
        const remainingChangeRequestStatuses = getRemainingChangeRequestStatuses(workflowConfiguration.changeRequestStatus);
        const response = await deployWorkflow(dispatch, workflowConfiguration.id, remainingChangeRequestStatuses, 0);

        if (response) {
            displaySuccessModal(
                dispatch,
                resources.requests.deployWorkflowChangeRequest.success.title,
                resources.requests.deployWorkflowChangeRequest.success.message
            );
        }

        await getWorkflowConfigurationsReduxRequest(dispatch)(
            companies,
            relyingParties
        );

        if (response) {
            onModalClose();
        }

        return response;
    };

    const onClickDeployChangesNow = () => {
        return clickWorkflowChangeRequestActionHandler(
            onSubmitDeployNowWorkflowChangeRequest,
            modalContext,
            workflowConfiguration.name,
            workflowConfiguration.id,
            workflowConfiguration.changeRequestStatus,
            ModalTypes.DeployNowWorkflowChangeRequest

        );
    };

    const onClickTransactionReport = () => {
        const queryParametersObject = {
            startDate: format(getTimeRange(TimeRangeTypes.TODAY).startDate, SERVER_DATE_TIME_FORMAT),
            endDate: format(getTimeRange(TimeRangeTypes.TODAY).endDate, SERVER_DATE_TIME_FORMAT),
            timeRangeType: TimeRangeTypes.TODAY,
            shouldSearchOnLoad: "true",
            workflowIds: workflowConfiguration.id
        };

        const queryParameters = createSearchParams(queryParametersObject).toString();
        openToNewTabUtil(`${REPORTS_TRANSACTIONS_URL}?${queryParameters}`, routerLocation.pathname);
    };

    const onClickWorkflowReport = () => {
        const queryParametersObject = {
            shouldSearchOnLoad: "true",
            startDate: format(getTimeRange(TimeRangeTypes.PAST_SEVEN_DAYS).startDate, SERVER_DATE_FORMAT),
            endDate: format(getTimeRange(TimeRangeTypes.PAST_SEVEN_DAYS).endDate, SERVER_DATE_FORMAT),
            timeRangeType: TimeRangeTypes.PAST_SEVEN_DAYS,
            workflowId: workflowConfiguration.id
        };

        const queryParameters = createSearchParams(queryParametersObject).toString();
        openToNewTabUtil(`${REPORTS_WORKFLOW_REPORT_URL}?${queryParameters}`, routerLocation.pathname);
    };

    const onClickFraudReport = () => {
        const queryParametersObject = {
            startDate: format(getTimeRange(TimeRangeTypes.PAST_SEVEN_DAYS).startDate, SERVER_DATE_FORMAT),
            endDate: format(getTimeRange(TimeRangeTypes.PAST_SEVEN_DAYS).endDate, SERVER_DATE_FORMAT),
            timeRangeType: TimeRangeTypes.PAST_SEVEN_DAYS,
            shouldSearchOnLoad: "true",
            rpServiceIds: ""
        };
        if (workflowConfiguration.rpServiceIDs.length > 0) {
            queryParametersObject.rpServiceIds = workflowConfiguration.rpServiceIDs.join(",")
        } else {
            delete queryParametersObject.rpServiceIds
        }

        const queryParameters = createSearchParams(queryParametersObject).toString();
        openToNewTabUtil(`${REPORTS_FRAUD_REPORT_URL}?${queryParameters}`, routerLocation.pathname);
    };

    const onClickManageServices = () => {
        let queryParameter = `workflowName=${workflowConfiguration.name}`;

        if (workflowConfiguration.relyingParty?.name) {
            queryParameter += `&relyingParty=${workflowConfiguration.relyingParty?.name}`;
        }

        openToNewTabUtil(`${VERIFICATION_SERVICES_URL}?${queryParameter}`, routerLocation.pathname);
    };

    const onClickRestoreWorkflow = () => {
        const renderRestoreWorkflowModal = (onModalClose: () => void) => {
            return (
                <RestoreVerificationWorkflowModal
                    onModalClose={onModalClose}
                    workflowConfiguration={workflowConfiguration}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.RestoreWorkflow,
            renderFunction: renderRestoreWorkflowModal
        });
    };

    const onClickExportWorkflowConfiguration = async () => {
        const renderSpinnerModal = (onModalClose: () => void) => {
            return (
                <SpinnerModal
                    onModalClose={onModalClose}
                    message="Fetching Workflow Data"
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ExportWorkflow,
            renderFunction: renderSpinnerModal
        });

        const { data, error, queryParameters } = await exportWorkflowConfigurationRequest(workflowConfiguration.id);
        if (data) {
            downloadWorkflowConfigurationFile(data.workflow?.name, data);
        } else if (error) {
            logAndDisplayErrorModal(
                dispatch,
                error,
                resources.requests.exportWorkflowConfiguration.error.title,
                resources.requests.exportWorkflowConfiguration.error.message,
                null,
                queryParameters
            );
        }

        modalContext.setModalState({
            modalType: undefined,
            renderFunction: undefined
        });
    };

    const onClickArchiveWorkflow = () => {
        const renderArchiveWorkflowModal = (onModalClose: () => void) => {
            return (
                <ArchiveVerificationWorkflowModal
                    onModalClose={onModalClose}
                    workflowConfiguration={workflowConfiguration}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.ArchiveWorkflow,
            renderFunction: renderArchiveWorkflowModal
        });
    };

    const onClickDeleteWorkflow = () => {
        const renderDeleteWorkflowModal = (onModalClose: () => void) => {
            return (
                <DeleteVerificationWorkflowModal
                    onModalClose={onModalClose}
                    workflowConfiguration={workflowConfiguration}
                />
            );
        };

        modalContext.setModalState({
            modalType: ModalTypes.DeleteWorkflow,
            renderFunction: renderDeleteWorkflowModal
        });
    };

    const workflowDropdownItems = useMemo(() => {
        const items: Array<CellDropdownItem> = [];
        const {
            status,
            permissions,
            changeRequestStatus,
            changeRequestType
        } = workflowConfiguration;
        const prefix = 'workflow-table-dropdown-item';

        if (status === EntityStatusTypes.INACTIVE) {
            if (permissions.canRestore) {
                items.push({
                    idSuffix: `${prefix}-restore`,
                    title: resources.tables.workflows.dropdownItems.restoreWorkflow.label,
                    text: resources.tables.workflows.dropdownItems.restoreWorkflow.label,
                    disabled: !canRestoreDueToLimits,
                    disabledText: resources.tables.workflows.dropdownItems.restoreWorkflow.disabledText,
                    onClick: onClickRestoreWorkflow
                });
            }

            if (permissions.canDelete) {
                items.push({
                    idSuffix: `${prefix}-delete-workflow`,
                    title: resources.tables.workflows.dropdownItems.deleteWorkflow.label,
                    text: resources.tables.workflows.dropdownItems.deleteWorkflow.label,
                    className: styles.archiveButton,
                    onClick: onClickDeleteWorkflow
                });
            }
        } else if (status === EntityStatusTypes.ACTIVE) {
            items.push(
                {
                    idSuffix: `${prefix}-execution-header`,
                    title: resources.common.dropdownItems.execution.label,
                    text: resources.common.dropdownItems.execution.label,
                    onClick: () => { },
                    isHeader: true
                },
                {
                    idSuffix: `${prefix}-try-now`,
                    title: resources.common.dropdownItems.tryNow.label,
                    text: resources.common.dropdownItems.tryNow.label,
                    onClick: onClickTryNow
                },
                {
                    idSuffix: `${prefix}-copy-link`,
                    title: resources.common.dropdownItems.copyLink.label,
                    text: resources.common.dropdownItems.copyLink.label,
                    onClick: onClickCopyLink
                },
                {
                    idSuffix: `${prefix}-send-email`,
                    title: resources.common.dropdownItems.sendEmail.label,
                    text: resources.common.dropdownItems.sendEmail.label,
                    onClick: onClickSendEmailWithLink
                }
            );

            items.push({
                idSuffix: `${prefix}-configuration-header`,
                title: resources.common.dropdownItems.configuration.label,
                text: resources.common.dropdownItems.configuration.label,
                onClick: () => { },
                isHeader: true
            });

            if (permissions.canView) {
                items.push({
                    idSuffix: 'open',
                    title: resources.pages.workflows.dropdownItems.open.label,
                    text: resources.pages.workflows.dropdownItems.open.label,
                    onClick: onClickOpen
                });
            }

            items.push({
                idSuffix: `${prefix}-manage-services`,
                title: resources.tables.workflows.dropdownItems.manageServices.label,
                text: resources.tables.workflows.dropdownItems.manageServices.label,
                onClick: onClickManageServices
            });

            if (
                permissions.canSubmit &&
                permissions.canReview &&
                permissions.canDeploy &&
                changeRequestStatus &&
                [
                    ChangeRequestStatusTypes.UNSUBMITTED,
                    ChangeRequestStatusTypes.SUBMITTED,
                    ChangeRequestStatusTypes.UNDER_REVIEW,
                    ChangeRequestStatusTypes.APPROVED
                ].includes(changeRequestStatus)
            ) {
                items.push({
                    idSuffix: `${prefix}-deploy-changes-now`,
                    title: resources.common.dropdownItems.deployChangesNow.label,
                    text: resources.common.dropdownItems.deployChangesNow.label,
                    onClick: onClickDeployChangesNow
                });
            }

            if (permissions.canModify && !changeRequestType && !changeRequestStatus) {
                items.push({
                    idSuffix: `${prefix}-create-modify-change-request`,
                    title: resources.common.dropdownItems.createModifyChangeRequest.label,
                    text: resources.common.dropdownItems.createModifyChangeRequest.label,
                    onClick: onClickCreateAModifyChangeRequest
                });
            }

            if (
                permissions.canCancel &&
                changeRequestType &&
                ![ChangeRequestStatusTypes.CANCELED, ChangeRequestStatusTypes.DEPLOYED].includes(changeRequestStatus)
            ) {
                items.push({
                    idSuffix: `${prefix}-cancel-change-request`,
                    title: resources.common.dropdownItems.cancelChangeRequest.label,
                    text: resources.common.dropdownItems.cancelChangeRequest.label,
                    onClick: onClickCancelChangeRequest
                });
            }

            if (permissions.canView) {
                items.push({
                    idSuffix: `${prefix}-export-workflow-configuration`,
                    title: resources.tables.workflows.dropdownItems.exportWorkflowConfiguration.label,
                    text: resources.tables.workflows.dropdownItems.exportWorkflowConfiguration.label,
                    onClick: onClickExportWorkflowConfiguration
                });
            }


            if (permissions.canArchive) {
                items.push({
                    idSuffix: `${prefix}-archive-workflow`,
                    title: resources.tables.workflows.dropdownItems.archiveWorkflow.label,
                    text: resources.tables.workflows.dropdownItems.archiveWorkflow.label,
                    className: styles.archiveButton,
                    onClick: onClickArchiveWorkflow
                });
            }

            if (permissions.canDelete) {
                items.push({
                    idSuffix: `${prefix}-delete-workflow`,
                    title: resources.tables.workflows.dropdownItems.deleteWorkflow.label,
                    text: resources.tables.workflows.dropdownItems.deleteWorkflow.label,
                    className: styles.archiveButton,
                    onClick: onClickDeleteWorkflow
                });
            }

            const reportItems: Array<CellDropdownItem> = [];

            if (session.userPermissions.canViewTransactionReport) {
                reportItems.push({
                    idSuffix: `${prefix}-transaction-report`,
                    title: resources.tables.workflows.dropdownItems.transactionReport.label,
                    text: resources.tables.workflows.dropdownItems.transactionReport.label,
                    onClick: onClickTransactionReport
                });
            }

            const rpProperties = axnAdminUserRPProperties.filter(rpProperty => rpProperty.relyingPartyID === workflowConfiguration.relyingParty?.id);

            const canViewRPWorkflowReport = (
                axnAdminUserRPProperties.length === 0 ||
                rpProperties.some(prop =>
                    prop.propertyName === UserPermissionTypes.VIEW_WORKFLOW_USAGE_DETAILS &&
                    isStringTrue(prop.propertyValue))
            );
            if (session.userPermissions.canViewWorkflowReport && canViewRPWorkflowReport) {
                reportItems.push({
                    idSuffix: `${prefix}-workflow-report`,
                    title: resources.tables.workflows.dropdownItems.workflowReport.label,
                    text: resources.tables.workflows.dropdownItems.workflowReport.label,
                    onClick: onClickWorkflowReport
                })
            }

            const canViewRPFraudReport = (
                axnAdminUserRPProperties.length === 0 ||
                rpProperties.some(prop =>
                    prop.propertyName === UserPermissionTypes.VIEW_USER_ASSERTION_CLAIM_RISK_SUBSET &&
                    isStringTrue(prop.propertyValue))
            );
            if (session.userPermissions.canViewFraudReport && canViewRPFraudReport) {
                reportItems.push({
                    idSuffix: `${prefix}-fraud-report`,
                    title: resources.tables.workflows.dropdownItems.fraudReport.label,
                    text: resources.tables.workflows.dropdownItems.fraudReport.label,
                    onClick: onClickFraudReport
                })
            }

            if (reportItems.length > 0) {
                items.push(
                    {
                        idSuffix: `${prefix}-report-header`,
                        title: resources.common.dropdownItems.reports.label,
                        text: resources.common.dropdownItems.reports.label,
                        onClick: () => { },
                        isHeader: true
                    },
                    ...reportItems
                );
            }
        }

        return items;
    }, [
        workflowConfiguration,
        canRestoreDueToLimits
    ]);

    return (
        <TableCellWithDropdown
            value={value}
            dropdownText={""}
            items={workflowDropdownItems}
            uniqueId={workflowConfiguration.id}
        />
    );
};